import {Fragment, useEffect, useMemo, useState} from "react";
import {connect} from "react-redux";
import {css, Theme} from "@emotion/react";
import styled from "@emotion/styled";
import {bindActionCreators, Dispatch} from "redux";

import {Button} from "@pg-design/button-module";
import {WideContent} from "@pg-design/grid-module";
import {backgroundColor, calculateRemSize, flex, flexDirection, mb, mt, p, pb, ph, pv, w100} from "@pg-design/helpers-css";
import {ArrowIcon, ChevronDownIcon, FiltersIcon} from "@pg-design/icons-module";
import {InputWrapper} from "@pg-design/inputs-module";
import {Loader} from "@pg-design/loader-module";
import {Pagination, PaginationSize} from "@pg-design/pagination-module";
import {getThemeBreakpoint} from "@pg-design/styles-strings";
import {Text} from "@pg-design/text-module";
import {FormActions} from "@pg-mono/form";
import {uniqBy} from "@pg-mono/nodash";
import {useUserDevice} from "@pg-mono/user-device";
import {Country} from "@pg-rp/common-types";

import {scrollToTop} from "../../app/components/ScrollToTop";
import {IRPStore} from "../../app/rp_reducer";
import {LeadModal} from "../../application/components/LeadModal";
import {MobileOptionsModal} from "../../atoms/MobileOptionsModal";
import {notifyBugsnag} from "../../errors/bugsnag/notify_bugsnag";
import {ErrorView} from "../../errors/views/ErrorView";
import {sortWrapper} from "../../offer/detail/components/property_list/OfferDetailPropertyListFilters";
import {Sort} from "../../offer/detail/components/sort/Sort";
import {SortOptions} from "../../offer/detail/components/sort/SortOptions";
import {sortOptions} from "../../offer/list/components/OfferListSort";
import {hitGTMPropertySelectItem} from "../../offer/tracking/ecommerce_events/property/hit_gtm_property_select_item";
import {hitGTMPropertyViewItemList} from "../../offer/tracking/ecommerce_events/property/hit_gtm_property_view_item_list";
import {propertyListSortOptions} from "../../property/constants/property_list_sort_options";
import {IPropertyListProperty} from "../../property/types/PropertyListProperty";
import {getHomeMatchSearchRegions} from "../../region/utils/get_homematch_regions";
import {fetchAllSearchLists, IFetchAllSearchListsParams} from "../../search/actions/fetch_search_all_action";
import {ISearchInputValue} from "../../search/components/ISearchInputValue";
import {SearchTab} from "../../search/types/SearchTab";
import {useHomeMatchPreferencesStorage} from "../../user/hooks/preferences/use_home_match_preferences_storage";
import {IValueLabel} from "../../variables/types/IValueLabel";
import {GTMEvent, propertyConfiguratorAnalytics} from "../analytics/events";
import {hitPropertyConfiguratorSearch} from "../analytics/hit_property_configurator_search";
import {fetchAllPropertiesCount} from "../api/fetch_all_properties_count";
import {fetchAllPropertiesFromOffer} from "../api/fetch_all_properties_from_offer";
import {fetchFirstProperties} from "../api/fetch_first_properties";
import {MobileFiltersModal} from "../components/filters/MobileFiltersModal";
import {PropertyConfiguratorFiltersForm} from "../components/filters/PropertyConfiguratorFiltersForm";
import {HorizontalPropertyBox} from "../components/HorizontalPropertyBox";
import {Map} from "../components/Map";
import {OfferHeader} from "../components/OfferHeader";
import {usePropertyConfiguratorFiltersAnalytics} from "../hooks/use_property_configurator_filters_analytics";
import {propertyConfiguratorSearchFormFields} from "../reducers/property_configurator_reducer";
import {IPropertyConfiguratorProperty} from "../types/IPropertyConfiguratorProperty";
import {PropertyConfiguratorSearchFormValuesType} from "../types/PropertyConfiguratorSearchFormValuesType";
import {getParams} from "../utils/get_property_api_params";
import {getValuesWithCurrentSearchFieldState} from "../utils/get_values_with_current_search_field_state";
import {handleFormChange} from "../utils/handle_form_change";
import {getPropertyConfiguratorStorageData} from "../utils/property_configurator_storage";
import {searchFormActions} from "./PropertyConfigurator";

interface IStateProps {
    formValues: PropertyConfiguratorSearchFormValuesType;
    propertyQuarters: IValueLabel[] | undefined;
}
interface IActionsProps {
    formActions: FormActions<Record<keyof typeof propertyConfiguratorSearchFormFields, unknown>>;
    fetchAllSearchLists: (searchInput: string, params: IFetchAllSearchListsParams) => Promise<SearchTab>;
}

interface IOwnProps {
    handleStoreValues: (values: PropertyConfiguratorSearchFormValuesType) => void;
}

interface IProps extends IStateProps, IActionsProps, IOwnProps {}

export const ResultsListC = (props: IProps) => {
    const [isDropdownOpen, setDropdownOpen] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [currentOfferPage, setCurrentOfferPage] = useState(1);
    const [maxPage, setMaxPage] = useState(1);
    const [maxOfferPage, setMaxOfferPage] = useState(1);
    const {isMobile} = useUserDevice();
    const [isLoading, setIsLoading] = useState(false);
    const [fetchingError, setFetchingError] = useState(false);
    const [firstProperties, setFirstProperties] = useState<IPropertyConfiguratorProperty[]>([]);
    const [allPropertiesCount, setAllPropertiesCount] = useState(0);
    const [propertiesFromOffer, setPropertiesFromOffer] = useState<IPropertyConfiguratorProperty[]>([]);
    const [propertiesInOfferCount, setPropertiesInOfferCount] = useState(0);
    const [sortValue, setSortValue] = useState(1);
    const [temporarySortValue, setTemporarySortValue] = useState(0);
    const [propertySort, setPropertySort] = useState(0);
    const [isMobileSortActive, setMobileSortActive] = useState(false);
    const [firstPropertiesParamsJSON, setFirstPropertiesParamsJSON] = useState("");
    const [filtersModalOpen, setFiltersModalOpen] = useState(false);
    const [_, setShouldRerender] = useState(0);

    const {putPreferences} = useHomeMatchPreferencesStorage();

    const {trackArea, trackFloors, trackRooms, trackWorldDirections, trackAdditionalAreas, trackKitchenType} = usePropertyConfiguratorFiltersAnalytics(
        GTMEvent.FLAT_CONFIG_SIDEBAR
    );

    const filterProps = {
        values: props.formValues,
        onChange: (value: Partial<PropertyConfiguratorSearchFormValuesType>) => {
            handleFormChange(value, props.formActions.update, props.formValues);

            trackRooms(value);
        }
    };

    const [expandedOffer, setExpandedOffer] = useState<null | IPropertyListProperty>(null);

    const propertiesWithUniqueSlugs = uniqBy(firstProperties, (item) => item.offer.slug);
    const sortOptionsVariant = useMemo(
        () => (expandedOffer ? propertyListSortOptions : sortOptions.map((option) => ({label: option.title, value: parseInt(option.value)}))),
        [expandedOffer]
    );

    const getFirstProperties = async (page: number, name?: string, value?: unknown) => {
        try {
            const values = {
                ...props.formValues,
                ...(name === "search" && {search: value}),
                ...(name === "price" && {price: value}),
                ...(name === "construction_end_date" && {construction_end_date: value})
            };
            const params = getParams(values, sortValue);
            const paramsWithPage = {...params, page};
            if (JSON.stringify(paramsWithPage) === firstPropertiesParamsJSON) {
                return;
            }
            scrollToTop();
            setFetchingError(false);
            setIsLoading(true);
            setFirstPropertiesParamsJSON(JSON.stringify(paramsWithPage));
            const data = await fetchFirstProperties(paramsWithPage);
            setFirstProperties(data.results);
            setMaxPage(Math.ceil(data.count / data.page_size));
            const newCount = await fetchAllPropertiesCount(params);
            hitPropertyConfiguratorSearch(values, data);
            setAllPropertiesCount(newCount);
            setIsLoading(false);
        } catch (error) {
            const message = "Error in fetchFirstProperties or fetchFirstPropertiesFlatsForSaleCount";
            setIsLoading(false);
            setFetchingError(true);
            notifyBugsnag(error, error instanceof Error ? error.message : message);
        }
    };

    const getPropertiesFromOffer = async (page: number, ignoreSort?: boolean) => {
        try {
            if (!expandedOffer) {
                return;
            }
            setFetchingError(false);
            setIsLoading(true);
            const propertySortedBy = (expandedOffer && propertyListSortOptions.find((option) => option.value === propertySort)?.slug) || (ignoreSort && "");
            const params = {
                ...getParams(getPropertyConfiguratorStorageData(), sortValue),
                page,
                ...(propertySortedBy ? {sort: propertySortedBy} : {})
            };
            const data = await fetchAllPropertiesFromOffer(params, expandedOffer.offer.id);
            setMaxOfferPage(Math.ceil(data.count / data.page_size));
            setPropertiesFromOffer(data.results);
            setPropertiesInOfferCount(data.count);
            setIsLoading(false);
            scrollToTop();
        } catch {
            setIsLoading(false);
            setFetchingError(true);
        }
    };

    const handleOfferClick = (offerId: string | number) => {
        setIsLoading(true);
        setPropertiesFromOffer([]);
        const propertySortedBy = expandedOffer && propertyListSortOptions.find((option) => option.value === propertySort)?.slug;
        const params = {...getParams(props.formValues, sortValue), page: 1, ...(propertySortedBy ? {sort: propertySortedBy} : {})};
        const indexOfMatchingProperty = firstProperties.findIndex(({offer}) => offer.id === offerId);
        if (indexOfMatchingProperty >= 0) {
            setExpandedOffer(firstProperties[indexOfMatchingProperty]);
        }
        fetchAllPropertiesFromOffer(params, offerId).then((data) => {
            setCurrentOfferPage(1);
            setPropertiesFromOffer(data.results);
            setIsLoading(false);
            setPropertiesInOfferCount(data.count);
        });
    };

    const discardModal = () => {
        if (sortValue !== 0) {
            setSortValue(sortValue);
        }
        setMobileSortActive(false);
    };

    const clearSort = () => {
        if (expandedOffer) {
            setPropertySort(0);
            setTemporarySortValue(0);
        }
        if (!expandedOffer) {
            setSortValue(1);
            setTemporarySortValue(1);
        }
        if (isMobile) {
            setMobileSortActive(false);
        }
        setDropdownOpen(false);
    };

    const saveSort = () => {
        if (isMobile) {
            setMobileSortActive(false);
        }
        if (expandedOffer) {
            setPropertySort(temporarySortValue);
        } else {
            setSortValue(temporarySortValue);
        }
        setDropdownOpen(false);
    };

    useEffect(() => {
        setTimeout(() => getFirstProperties(currentPage), 10);
    }, [currentPage, sortValue]);

    useEffect(() => {
        getPropertiesFromOffer(currentOfferPage);
    }, [currentOfferPage, propertySort]);

    useEffect(() => {
        if (!expandedOffer) {
            setTemporarySortValue(sortValue);
            setMaxOfferPage(1);
            setCurrentOfferPage(1);
        }
        if (expandedOffer) {
            setTemporarySortValue(propertySort);
            getPropertiesFromOffer(1);
            setPropertiesFromOffer([]);
        }
    }, [expandedOffer]);

    useEffect(() => {
        if (!expandedOffer && sortValue !== temporarySortValue) {
            setTemporarySortValue(sortValue);
        }
        if (expandedOffer && propertySort !== temporarySortValue) {
            setTemporarySortValue(propertySort);
        }
    }, [isDropdownOpen]);

    useEffect(() => {
        //This has to be here in order to correctly render React-range's thumbs. Otherwise it tries to calculate thumbs' position when animation is started resulting in bad position. If we wait for modal to finish opening, then it's fine
        setTimeout(() => setShouldRerender((prev) => prev + 1), 250);
    }, [filtersModalOpen]);

    const gtmMeta = {
        listName: "Dostępne mieszkania w tej lokalizacji",
        listId: "konfigurator_mieszkan"
    };
    useEffect(() => {
        if (propertiesWithUniqueSlugs.length > 0) {
            hitGTMPropertyViewItemList({
                properties: propertiesWithUniqueSlugs,
                ...gtmMeta
            });
        }
    }, [propertiesWithUniqueSlugs?.map((property) => property.id).join(",")]);

    useEffect(() => {
        if (expandedOffer) {
            hitGTMPropertySelectItem({
                offer: {
                    ...expandedOffer.offer,
                    construction_date_range: {
                        upper: expandedOffer.construction_end_date
                    }
                },
                property: expandedOffer,
                listName: gtmMeta.listName,
                listId: gtmMeta.listId
            });
        }
    }, [expandedOffer]);

    useEffect(() => {
        if (expandedOffer && propertiesFromOffer.length > 0) {
            hitGTMPropertyViewItemList({
                properties: propertiesFromOffer,
                ...gtmMeta
            });
        }
    }, [expandedOffer, propertiesFromOffer.map((property) => property.id).join(",")]);

    if (fetchingError) {
        return <ErrorView />;
    }

    const validateAreaFilter = () => {
        const lowerValue = parseFloat(filterProps.values.area.lower);
        const upperValue = parseFloat(filterProps.values.area.upper);

        return !isNaN(lowerValue) && !isNaN(upperValue) && lowerValue > upperValue;
    };

    const searchValue = props.formValues.search as ISearchInputValue;
    const isPoland = searchValue.tabType === SearchTab.Regions && searchValue.regions.every((region) => region.country === Country.POLAND);

    return (
        <>
            <WideContent css={wrapper}>
                <div css={preferencesSidebar}>
                    <Text variant="headline_3">Twoje preferencje</Text>
                    <PropertyConfiguratorFiltersForm {...filterProps} />

                    <Button
                        disabled={isLoading}
                        size="big"
                        variant="filled_primary"
                        onClick={() => {
                            if (isPoland) {
                                putPreferences({rooms: props.formValues.rooms, budget: Number(props.formValues.price.upper)});
                            }
                            if (validateAreaFilter()) {
                                // prevent from saving if area filter is invalid
                                return;
                            }
                            if (expandedOffer) {
                                setExpandedOffer(null);
                            }
                            if (currentPage !== 1) {
                                setCurrentPage(1);
                            } else {
                                getFirstProperties(1);
                            }
                            props.handleStoreValues(props.formValues);
                            propertyConfiguratorAnalytics.gtm.propertyConfiguratorEvent(GTMEvent.FLAT_CONFIG_CHANGE);
                            trackArea();
                            trackFloors();
                            trackWorldDirections();
                            trackAdditionalAreas();
                            trackKitchenType();
                        }}
                    >
                        Zapisz
                    </Button>
                </div>
                <div css={[flex(), flexDirection("column"), w100, pb(4)]}>
                    {expandedOffer ? (
                        <OfferHeader
                            handleBackClick={() => {
                                propertyConfiguratorAnalytics.gtm.propertyConfiguratorEvent(GTMEvent.FLAT_CONFIG_BACK);
                                setExpandedOffer(null);
                            }}
                            images={expandedOffer.offer?.main_image}
                            name={expandedOffer.offer.name}
                            address={expandedOffer.offer.address}
                            vendorLogo={expandedOffer.offer.vendor.logo?.v_log_160x120}
                            onChange={props.formActions.update}
                            values={props.formValues}
                            formActions={props.formActions}
                            onEditClick={setFiltersModalOpen}
                        />
                    ) : (
                        <Map
                            fetchAllSearchLists={props.fetchAllSearchLists}
                            onChange={props.formActions.update}
                            values={props.formValues}
                            formActions={props.formActions}
                            handleChange={(name, value) => {
                                if (name === "search") {
                                    const searchValue = value as ISearchInputValue;
                                    const regionsSelected = searchValue.tabType === SearchTab.Regions && searchValue.regions;

                                    if (regionsSelected) {
                                        putPreferences({
                                            searchRegions: getHomeMatchSearchRegions(regionsSelected)
                                        });
                                    }
                                }

                                propertyConfiguratorAnalytics.gtm.propertyConfiguratorEvent(GTMEvent.FLAT_CONFIG_CHANGE);
                                setCurrentPage(1);
                                getFirstProperties(1, name, value);
                                const values = getValuesWithCurrentSearchFieldState(props.formValues, name, value);
                                props.handleStoreValues(values);
                            }}
                            properties={firstProperties}
                            handleOfferClick={handleOfferClick}
                            onEditClick={setFiltersModalOpen}
                        />
                    )}
                    {isLoading && <Loader size="md" css={p(12.5)} />}
                    {!isLoading && (
                        <>
                            <div css={headerStyle}>
                                <Text variant="headline_4">
                                    {expandedOffer
                                        ? `Dostępne mieszkania w tej inwestycji (${propertiesInOfferCount})`
                                        : `Dostępne mieszkania w tej lokalizacji (${allPropertiesCount || 0})`}
                                </Text>
                                <HeaderFilterWrapper reverseDirection={!!expandedOffer}>
                                    {/* @deprecated Comment code releated with displaying "Mieszkania i domy" tab due to RP-217 requirements
                                    this can be delted entirely in future if we will keep this approach */}
                                    {/* {!expandedOffer && (
                                        <ListingTypeTabs
                                            filters={props.formValues}
                                            additionalParams={{sort: temporarySortValue}}
                                            currentListingType={ListingType.PROPERTIES}
                                            offerType={OfferType.FLAT}
                                        />
                                    )} */}
                                    <InputWrapper css={[sortWrapper, inputWrapper]}>
                                        <Sort
                                            isDropdownOpen={isDropdownOpen}
                                            setDropdownOpen={setDropdownOpen}
                                            isMobile={isMobile as boolean}
                                            setDesktopFilterBlendActive={() => null}
                                            currentValue={temporarySortValue}
                                            setCurrentValue={setTemporarySortValue}
                                            sortOptions={sortOptionsVariant}
                                            rightDropdown
                                            dropdownShadow
                                            disableClearAction
                                            closeOnOutsideClick
                                        >
                                            <SortOptions
                                                currentValue={temporarySortValue}
                                                setCurrentValue={setTemporarySortValue}
                                                sortOptions={sortOptionsVariant}
                                                onCancel={clearSort}
                                                onSave={saveSort}
                                            />
                                        </Sort>
                                    </InputWrapper>
                                </HeaderFilterWrapper>
                                {isMobile && (
                                    <>
                                        <Button
                                            css={[w100, mt(4)]}
                                            variant="outlined_secondary"
                                            iconLeft={FiltersIcon}
                                            onClick={() => setMobileSortActive((prev) => !prev)}
                                        >
                                            Sortuj
                                        </Button>
                                        <MobileOptionsModal modalHeading="Sortuj" isOpen={isMobileSortActive} onModalClose={discardModal} css={modalStyle}>
                                            <SortOptions
                                                currentValue={temporarySortValue}
                                                setCurrentValue={setTemporarySortValue}
                                                sortOptions={sortOptionsVariant}
                                                onCancel={clearSort}
                                                onSave={saveSort}
                                            />
                                        </MobileOptionsModal>
                                    </>
                                )}
                            </div>
                            <div css={resultsWrapper}>
                                {!expandedOffer &&
                                    propertiesWithUniqueSlugs.map((property, index) => (
                                        <Fragment key={index}>
                                            <HorizontalPropertyBox property={property} gtmMeta={gtmMeta} />
                                            {property.matching_properties_count > 1 && (
                                                <Button
                                                    variant="none_secondary"
                                                    onClick={() => {
                                                        setPropertiesFromOffer([]);
                                                        setExpandedOffer(property);
                                                        propertyConfiguratorAnalytics.gtm.propertyConfiguratorEvent(GTMEvent.FLAT_CONFIG_MORE);
                                                    }}
                                                    iconLeft={ChevronDownIcon}
                                                    css={bottomButton}
                                                >
                                                    Pokaż jeszcze {property.matching_properties_count - 1} z tej inwestycji
                                                </Button>
                                            )}
                                        </Fragment>
                                    ))}
                                {expandedOffer &&
                                    propertiesFromOffer.map((property, index) => <HorizontalPropertyBox property={property} key={index} gtmMeta={gtmMeta} />)}
                            </div>
                            <Pagination
                                pageCount={expandedOffer ? maxOfferPage : maxPage}
                                currentPage={expandedOffer ? currentOfferPage : currentPage}
                                onPageChange={expandedOffer ? setCurrentOfferPage : setCurrentPage}
                                size={isMobile ? PaginationSize.MD : PaginationSize.LG}
                                siblingsCount={isMobile ? 0 : 1}
                            />
                            {expandedOffer && (
                                <Button
                                    variant="outlined_secondary"
                                    iconLeft={ArrowIcon}
                                    onClick={() => {
                                        scrollToTop();
                                        propertyConfiguratorAnalytics.gtm.propertyConfiguratorEvent(GTMEvent.FLAT_CONFIG_BACK);
                                        setExpandedOffer(null);
                                    }}
                                    css={backToLocationButton}
                                >
                                    Powrót do lokalizacji
                                </Button>
                            )}
                        </>
                    )}
                </div>
                <LeadModal />
                <MobileFiltersModal
                    isOpen={filtersModalOpen}
                    onClose={setFiltersModalOpen}
                    handleChange={(name, value) => {
                        propertyConfiguratorAnalytics.gtm.propertyConfiguratorEvent(GTMEvent.FLAT_CONFIG_CHANGE);
                        setCurrentPage(1);
                        getFirstProperties(1, name, value);
                        const values = getValuesWithCurrentSearchFieldState(props.formValues, name, value);
                        props.handleStoreValues(values);
                    }}
                    {...filterProps}
                />
            </WideContent>
        </>
    );
};

export const ResultsList = connect(mapStateToProps, mapActionsToProps)(ResultsListC);

function mapActionsToProps(dispatch: Dispatch): IActionsProps {
    // fetch search lists for property configurator
    const searchContentParams = {
        googlePOIs: false,
        regions: true,
        offers: false,
        vendors: false
    };
    return {
        formActions: bindActionCreators({...searchFormActions}, dispatch),
        ...bindActionCreators({fetchAllSearchLists: (searchInput: string) => fetchAllSearchLists(searchInput, {searchContent: searchContentParams})}, dispatch)
    };
}

function mapStateToProps(state: IRPStore): IStateProps {
    return {
        formValues: state.propertyConfigurator.formValues,
        propertyQuarters: state.variables.data?.property_quarters
    };
}

const wrapper = css`
    ${flex()};
    gap: ${calculateRemSize(3)};
`;

const preferencesSidebar = (theme: Theme) => css`
    ${flex()};
    ${flexDirection("column")};
    width: 100%;
    max-width: 384px;
    ${backgroundColor("white")};
    ${ph(3)};
    ${pv(4)};
    gap: ${calculateRemSize(4)};

    .checkboxesSegmentStyle {
        ${mt(3)};
    }

    @media (max-width: ${theme.breakpoints.md}) {
        display: none;
    }
`;

const resultsWrapper = css`
    ${flex()};
    ${flexDirection("column")};
    ${mb(5)};
    gap: ${calculateRemSize(2)};
`;

const bottomButton = css`
    align-self: center;
    max-height: ${calculateRemSize(5)};
    ${mb(1)};
`;

const headerStyle = css`
    ${mt(5)};
    ${mb(4)};
`;

const HeaderFilterWrapper = styled.div<{reverseDirection: boolean}>`
    ${flex("flex-end", "flex-end")};
    flex-direction: column;
    ${mt(4)};
    gap: ${calculateRemSize(4)};

    @media (min-width: ${(props) => props.theme.breakpoints.md}) {
        flex-direction: ${(props) => (props.reverseDirection ? "row-reverse" : "row")};
    }
`;

const inputWrapper = (theme: Theme) => css`
    @media (max-width: ${theme.breakpoints.md}) {
        display: none;
    }
`;

const backToLocationButton = css`
    align-self: center;
    ${mt(3)};
`;

const modalStyle = css`
    @media (min-width: ${getThemeBreakpoint().sm}) {
        min-width: 360px;
        max-height: 500px;
    }
`;
import {Fragment, useEffect, useMemo, useState} from "react";
import {connect} from "react-redux";
import {css, Theme} from "@emotion/react";
import styled from "@emotion/styled";
import {bindActionCreators, Dispatch} from "redux";

import {Button} from "@pg-design/button-module";
import {WideContent} from "@pg-design/grid-module";
import {backgroundColor, calculateRemSize, flex, flexDirection, mb, mt, p, pb, ph, pv, w100} from "@pg-design/helpers-css";
import {ArrowIcon, ChevronDownIcon, FiltersIcon} from "@pg-design/icons-module";
import {InputWrapper} from "@pg-design/inputs-module";
import {Loader} from "@pg-design/loader-module";
import {Pagination, PaginationSize} from "@pg-design/pagination-module";
import {getThemeBreakpoint} from "@pg-design/styles-strings";
import {Text} from "@pg-design/text-module";
import {FormActions} from "@pg-mono/form";
import {uniqBy} from "@pg-mono/nodash";
import {useUserDevice} from "@pg-mono/user-device";
import {Country} from "@pg-rp/common-types";

import {scrollToTop} from "../../app/components/ScrollToTop";
import {IRPStore} from "../../app/rp_reducer";
import {LeadModal} from "../../application/components/LeadModal";
import {MobileOptionsModal} from "../../atoms/MobileOptionsModal";
import {notifyBugsnag} from "../../errors/bugsnag/notify_bugsnag";
import {ErrorView} from "../../errors/views/ErrorView";
import {sortWrapper} from "../../offer/detail/components/property_list/OfferDetailPropertyListFilters";
import {Sort} from "../../offer/detail/components/sort/Sort";
import {SortOptions} from "../../offer/detail/components/sort/SortOptions";
import {sortOptions} from "../../offer/list/components/OfferListSort";
import {hitGTMPropertySelectItem} from "../../offer/tracking/ecommerce_events/property/hit_gtm_property_select_item";
import {hitGTMPropertyViewItemList} from "../../offer/tracking/ecommerce_events/property/hit_gtm_property_view_item_list";
import {propertyListSortOptions} from "../../property/constants/property_list_sort_options";
import {IPropertyListProperty} from "../../property/types/PropertyListProperty";
import {getHomeMatchSearchRegions} from "../../region/utils/get_homematch_regions";
import {fetchAllSearchLists, IFetchAllSearchListsParams} from "../../search/actions/fetch_search_all_action";
import {ISearchInputValue} from "../../search/components/ISearchInputValue";
import {SearchTab} from "../../search/types/SearchTab";
import {useHomeMatchPreferencesStorage} from "../../user/hooks/preferences/use_home_match_preferences_storage";
import {IValueLabel} from "../../variables/types/IValueLabel";
import {GTMEvent, propertyConfiguratorAnalytics} from "../analytics/events";
import {hitPropertyConfiguratorSearch} from "../analytics/hit_property_configurator_search";
import {fetchAllPropertiesCount} from "../api/fetch_all_properties_count";
import {fetchAllPropertiesFromOffer} from "../api/fetch_all_properties_from_offer";
import {fetchFirstProperties} from "../api/fetch_first_properties";
import {MobileFiltersModal} from "../components/filters/MobileFiltersModal";
import {PropertyConfiguratorFiltersForm} from "../components/filters/PropertyConfiguratorFiltersForm";
import {HorizontalPropertyBox} from "../components/HorizontalPropertyBox";
import {Map} from "../components/Map";
import {OfferHeader} from "../components/OfferHeader";
import {usePropertyConfiguratorFiltersAnalytics} from "../hooks/use_property_configurator_filters_analytics";
import {propertyConfiguratorSearchFormFields} from "../reducers/property_configurator_reducer";
import {IPropertyConfiguratorProperty} from "../types/IPropertyConfiguratorProperty";
import {PropertyConfiguratorSearchFormValuesType} from "../types/PropertyConfiguratorSearchFormValuesType";
import {getParams} from "../utils/get_property_api_params";
import {getValuesWithCurrentSearchFieldState} from "../utils/get_values_with_current_search_field_state";
import {handleFormChange} from "../utils/handle_form_change";
import {getPropertyConfiguratorStorageData} from "../utils/property_configurator_storage";
import {searchFormActions} from "./PropertyConfigurator";

interface IStateProps {
    formValues: PropertyConfiguratorSearchFormValuesType;
    propertyQuarters: IValueLabel[] | undefined;
}
interface IActionsProps {
    formActions: FormActions<Record<keyof typeof propertyConfiguratorSearchFormFields, unknown>>;
    fetchAllSearchLists: (searchInput: string, params: IFetchAllSearchListsParams) => Promise<SearchTab>;
}

interface IOwnProps {
    handleStoreValues: (values: PropertyConfiguratorSearchFormValuesType) => void;
}

interface IProps extends IStateProps, IActionsProps, IOwnProps {}

export const ResultsListC = (props: IProps) => {
    const [isDropdownOpen, setDropdownOpen] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [currentOfferPage, setCurrentOfferPage] = useState(1);
    const [maxPage, setMaxPage] = useState(1);
    const [maxOfferPage, setMaxOfferPage] = useState(1);
    const {isMobile} = useUserDevice();
    const [isLoading, setIsLoading] = useState(false);
    const [fetchingError, setFetchingError] = useState(false);
    const [firstProperties, setFirstProperties] = useState<IPropertyConfiguratorProperty[]>([]);
    const [allPropertiesCount, setAllPropertiesCount] = useState(0);
    const [propertiesFromOffer, setPropertiesFromOffer] = useState<IPropertyConfiguratorProperty[]>([]);
    const [propertiesInOfferCount, setPropertiesInOfferCount] = useState(0);
    const [sortValue, setSortValue] = useState(1);
    const [temporarySortValue, setTemporarySortValue] = useState(0);
    const [propertySort, setPropertySort] = useState(0);
    const [isMobileSortActive, setMobileSortActive] = useState(false);
    const [firstPropertiesParamsJSON, setFirstPropertiesParamsJSON] = useState("");
    const [filtersModalOpen, setFiltersModalOpen] = useState(false);
    const [_, setShouldRerender] = useState(0);

    const {putPreferences} = useHomeMatchPreferencesStorage();

    const {trackArea, trackFloors, trackRooms, trackWorldDirections, trackAdditionalAreas, trackKitchenType} = usePropertyConfiguratorFiltersAnalytics(
        GTMEvent.FLAT_CONFIG_SIDEBAR
    );

    const filterProps = {
        values: props.formValues,
        onChange: (value: Partial<PropertyConfiguratorSearchFormValuesType>) => {
            handleFormChange(value, props.formActions.update, props.formValues);

            trackRooms(value);
        }
    };

    const [expandedOffer, setExpandedOffer] = useState<null | IPropertyListProperty>(null);

    const propertiesWithUniqueSlugs = uniqBy(firstProperties, (item) => item.offer.slug);
    const sortOptionsVariant = useMemo(
        () => (expandedOffer ? propertyListSortOptions : sortOptions.map((option) => ({label: option.title, value: parseInt(option.value)}))),
        [expandedOffer]
    );

    const getFirstProperties = async (page: number, name?: string, value?: unknown) => {
        try {
            const values = {
                ...props.formValues,
                ...(name === "search" && {search: value}),
                ...(name === "price" && {price: value}),
                ...(name === "construction_end_date" && {construction_end_date: value})
            };
            const params = getParams(values, sortValue);
            const paramsWithPage = {...params, page};
            if (JSON.stringify(paramsWithPage) === firstPropertiesParamsJSON) {
                return;
            }
            scrollToTop();
            setFetchingError(false);
            setIsLoading(true);
            setFirstPropertiesParamsJSON(JSON.stringify(paramsWithPage));
            const data = await fetchFirstProperties(paramsWithPage);
            setFirstProperties(data.results);
            setMaxPage(Math.ceil(data.count / data.page_size));
            const newCount = await fetchAllPropertiesCount(params);
            hitPropertyConfiguratorSearch(values, data);
            setAllPropertiesCount(newCount);
            setIsLoading(false);
        } catch (error) {
            const message = "Error in fetchFirstProperties or fetchFirstPropertiesFlatsForSaleCount";
            setIsLoading(false);
            setFetchingError(true);
            notifyBugsnag(error, error instanceof Error ? error.message : message);
        }
    };

    const getPropertiesFromOffer = async (page: number, ignoreSort?: boolean) => {
        try {
            if (!expandedOffer) {
                return;
            }
            setFetchingError(false);
            setIsLoading(true);
            const propertySortedBy = (expandedOffer && propertyListSortOptions.find((option) => option.value === propertySort)?.slug) || (ignoreSort && "");
            const params = {
                ...getParams(getPropertyConfiguratorStorageData(), sortValue),
                page,
                ...(propertySortedBy ? {sort: propertySortedBy} : {})
            };
            const data = await fetchAllPropertiesFromOffer(params, expandedOffer.offer.id);
            setMaxOfferPage(Math.ceil(data.count / data.page_size));
            setPropertiesFromOffer(data.results);
            setPropertiesInOfferCount(data.count);
            setIsLoading(false);
            scrollToTop();
        } catch {
            setIsLoading(false);
            setFetchingError(true);
        }
    };

    const handleOfferClick = (offerId: string | number) => {
        setIsLoading(true);
        setPropertiesFromOffer([]);
        const propertySortedBy = expandedOffer && propertyListSortOptions.find((option) => option.value === propertySort)?.slug;
        const params = {...getParams(props.formValues, sortValue), page: 1, ...(propertySortedBy ? {sort: propertySortedBy} : {})};
        const indexOfMatchingProperty = firstProperties.findIndex(({offer}) => offer.id === offerId);
        if (indexOfMatchingProperty >= 0) {
            setExpandedOffer(firstProperties[indexOfMatchingProperty]);
        }
        fetchAllPropertiesFromOffer(params, offerId).then((data) => {
            setCurrentOfferPage(1);
            setPropertiesFromOffer(data.results);
            setIsLoading(false);
            setPropertiesInOfferCount(data.count);
        });
    };

    const discardModal = () => {
        if (sortValue !== 0) {
            setSortValue(sortValue);
        }
        setMobileSortActive(false);
    };

    const clearSort = () => {
        if (expandedOffer) {
            setPropertySort(0);
            setTemporarySortValue(0);
        }
        if (!expandedOffer) {
            setSortValue(1);
            setTemporarySortValue(1);
        }
        if (isMobile) {
            setMobileSortActive(false);
        }
        setDropdownOpen(false);
    };

    const saveSort = () => {
        if (isMobile) {
            setMobileSortActive(false);
        }
        if (expandedOffer) {
            setPropertySort(temporarySortValue);
        } else {
            setSortValue(temporarySortValue);
        }
        setDropdownOpen(false);
    };

    useEffect(() => {
        setTimeout(() => getFirstProperties(currentPage), 10);
    }, [currentPage, sortValue]);

    useEffect(() => {
        getPropertiesFromOffer(currentOfferPage);
    }, [currentOfferPage, propertySort]);

    useEffect(() => {
        if (!expandedOffer) {
            setTemporarySortValue(sortValue);
            setMaxOfferPage(1);
            setCurrentOfferPage(1);
        }
        if (expandedOffer) {
            setTemporarySortValue(propertySort);
            getPropertiesFromOffer(1);
            setPropertiesFromOffer([]);
        }
    }, [expandedOffer]);

    useEffect(() => {
        if (!expandedOffer && sortValue !== temporarySortValue) {
            setTemporarySortValue(sortValue);
        }
        if (expandedOffer && propertySort !== temporarySortValue) {
            setTemporarySortValue(propertySort);
        }
    }, [isDropdownOpen]);

    useEffect(() => {
        //This has to be here in order to correctly render React-range's thumbs. Otherwise it tries to calculate thumbs' position when animation is started resulting in bad position. If we wait for modal to finish opening, then it's fine
        setTimeout(() => setShouldRerender((prev) => prev + 1), 250);
    }, [filtersModalOpen]);

    const gtmMeta = {
        listName: "Dostępne mieszkania w tej lokalizacji",
        listId: "konfigurator_mieszkan"
    };
    useEffect(() => {
        if (propertiesWithUniqueSlugs.length > 0) {
            hitGTMPropertyViewItemList({
                properties: propertiesWithUniqueSlugs,
                ...gtmMeta
            });
        }
    }, [propertiesWithUniqueSlugs?.map((property) => property.id).join(",")]);

    useEffect(() => {
        if (expandedOffer) {
            hitGTMPropertySelectItem({
                offer: {
                    ...expandedOffer.offer,
                    construction_date_range: {
                        upper: expandedOffer.construction_end_date
                    }
                },
                property: expandedOffer,
                listName: gtmMeta.listName,
                listId: gtmMeta.listId
            });
        }
    }, [expandedOffer]);

    useEffect(() => {
        if (expandedOffer && propertiesFromOffer.length > 0) {
            hitGTMPropertyViewItemList({
                properties: propertiesFromOffer,
                ...gtmMeta
            });
        }
    }, [expandedOffer, propertiesFromOffer.map((property) => property.id).join(",")]);

    if (fetchingError) {
        return <ErrorView />;
    }

    const validateAreaFilter = () => {
        const lowerValue = parseFloat(filterProps.values.area.lower);
        const upperValue = parseFloat(filterProps.values.area.upper);

        return !isNaN(lowerValue) && !isNaN(upperValue) && lowerValue > upperValue;
    };

    const searchValue = props.formValues.search as ISearchInputValue;
    const isPoland = searchValue.tabType === SearchTab.Regions && searchValue.regions.every((region) => region.country === Country.POLAND);

    return (
        <>
            <WideContent css={wrapper}>
                <div css={preferencesSidebar}>
                    <Text variant="headline_3">Twoje preferencje</Text>
                    <PropertyConfiguratorFiltersForm {...filterProps} />

                    <Button
                        disabled={isLoading}
                        size="big"
                        variant="filled_primary"
                        onClick={() => {
                            if (isPoland) {
                                putPreferences({rooms: props.formValues.rooms, budget: Number(props.formValues.price.upper)});
                            }
                            if (validateAreaFilter()) {
                                // prevent from saving if area filter is invalid
                                return;
                            }
                            if (expandedOffer) {
                                setExpandedOffer(null);
                            }
                            if (currentPage !== 1) {
                                setCurrentPage(1);
                            } else {
                                getFirstProperties(1);
                            }
                            props.handleStoreValues(props.formValues);
                            propertyConfiguratorAnalytics.gtm.propertyConfiguratorEvent(GTMEvent.FLAT_CONFIG_CHANGE);
                            trackArea();
                            trackFloors();
                            trackWorldDirections();
                            trackAdditionalAreas();
                            trackKitchenType();
                        }}
                    >
                        Zapisz
                    </Button>
                </div>
                <div css={[flex(), flexDirection("column"), w100, pb(4)]}>
                    {expandedOffer ? (
                        <OfferHeader
                            handleBackClick={() => {
                                propertyConfiguratorAnalytics.gtm.propertyConfiguratorEvent(GTMEvent.FLAT_CONFIG_BACK);
                                setExpandedOffer(null);
                            }}
                            images={expandedOffer.offer?.main_image}
                            name={expandedOffer.offer.name}
                            address={expandedOffer.offer.address}
                            vendorLogo={expandedOffer.offer.vendor.logo?.v_log_160x120}
                            onChange={props.formActions.update}
                            values={props.formValues}
                            formActions={props.formActions}
                            onEditClick={setFiltersModalOpen}
                        />
                    ) : (
                        <Map
                            fetchAllSearchLists={props.fetchAllSearchLists}
                            onChange={props.formActions.update}
                            values={props.formValues}
                            formActions={props.formActions}
                            handleChange={(name, value) => {
                                if (name === "search") {
                                    const searchValue = value as ISearchInputValue;
                                    const regionsSelected = searchValue.tabType === SearchTab.Regions && searchValue.regions;

                                    if (regionsSelected) {
                                        putPreferences({
                                            searchRegions: getHomeMatchSearchRegions(regionsSelected)
                                        });
                                    }
                                }

                                propertyConfiguratorAnalytics.gtm.propertyConfiguratorEvent(GTMEvent.FLAT_CONFIG_CHANGE);
                                setCurrentPage(1);
                                getFirstProperties(1, name, value);
                                const values = getValuesWithCurrentSearchFieldState(props.formValues, name, value);
                                props.handleStoreValues(values);
                            }}
                            properties={firstProperties}
                            handleOfferClick={handleOfferClick}
                            onEditClick={setFiltersModalOpen}
                        />
                    )}
                    {isLoading && <Loader size="md" css={p(12.5)} />}
                    {!isLoading && (
                        <>
                            <div css={headerStyle}>
                                <Text variant="headline_4">
                                    {expandedOffer
                                        ? `Dostępne mieszkania w tej inwestycji (${propertiesInOfferCount})`
                                        : `Dostępne mieszkania w tej lokalizacji (${allPropertiesCount || 0})`}
                                </Text>
                                <HeaderFilterWrapper reverseDirection={!!expandedOffer}>
                                    {/* @deprecated Comment code releated with displaying "Mieszkania i domy" tab due to RP-217 requirements
                                    this can be delted entirely in future if we will keep this approach */}
                                    {/* {!expandedOffer && (
                                        <ListingTypeTabs
                                            filters={props.formValues}
                                            additionalParams={{sort: temporarySortValue}}
                                            currentListingType={ListingType.PROPERTIES}
                                            offerType={OfferType.FLAT}
                                        />
                                    )} */}
                                    <InputWrapper css={[sortWrapper, inputWrapper]}>
                                        <Sort
                                            isDropdownOpen={isDropdownOpen}
                                            setDropdownOpen={setDropdownOpen}
                                            isMobile={isMobile as boolean}
                                            setDesktopFilterBlendActive={() => null}
                                            currentValue={temporarySortValue}
                                            setCurrentValue={setTemporarySortValue}
                                            sortOptions={sortOptionsVariant}
                                            rightDropdown
                                            dropdownShadow
                                            disableClearAction
                                            closeOnOutsideClick
                                        >
                                            <SortOptions
                                                currentValue={temporarySortValue}
                                                setCurrentValue={setTemporarySortValue}
                                                sortOptions={sortOptionsVariant}
                                                onCancel={clearSort}
                                                onSave={saveSort}
                                            />
                                        </Sort>
                                    </InputWrapper>
                                </HeaderFilterWrapper>
                                {isMobile && (
                                    <>
                                        <Button
                                            css={[w100, mt(4)]}
                                            variant="outlined_secondary"
                                            iconLeft={FiltersIcon}
                                            onClick={() => setMobileSortActive((prev) => !prev)}
                                        >
                                            Sortuj
                                        </Button>
                                        <MobileOptionsModal modalHeading="Sortuj" isOpen={isMobileSortActive} onModalClose={discardModal} css={modalStyle}>
                                            <SortOptions
                                                currentValue={temporarySortValue}
                                                setCurrentValue={setTemporarySortValue}
                                                sortOptions={sortOptionsVariant}
                                                onCancel={clearSort}
                                                onSave={saveSort}
                                            />
                                        </MobileOptionsModal>
                                    </>
                                )}
                            </div>
                            <div css={resultsWrapper}>
                                {!expandedOffer &&
                                    propertiesWithUniqueSlugs.map((property, index) => (
                                        <Fragment key={index}>
                                            <HorizontalPropertyBox property={property} gtmMeta={gtmMeta} />
                                            {property.matching_properties_count > 1 && (
                                                <Button
                                                    variant="none_secondary"
                                                    onClick={() => {
                                                        setPropertiesFromOffer([]);
                                                        setExpandedOffer(property);
                                                        propertyConfiguratorAnalytics.gtm.propertyConfiguratorEvent(GTMEvent.FLAT_CONFIG_MORE);
                                                    }}
                                                    iconLeft={ChevronDownIcon}
                                                    css={bottomButton}
                                                >
                                                    Pokaż jeszcze {property.matching_properties_count - 1} z tej inwestycji
                                                </Button>
                                            )}
                                        </Fragment>
                                    ))}
                                {expandedOffer &&
                                    propertiesFromOffer.map((property, index) => <HorizontalPropertyBox property={property} key={index} gtmMeta={gtmMeta} />)}
                            </div>
                            <Pagination
                                pageCount={expandedOffer ? maxOfferPage : maxPage}
                                currentPage={expandedOffer ? currentOfferPage : currentPage}
                                onPageChange={expandedOffer ? setCurrentOfferPage : setCurrentPage}
                                size={isMobile ? PaginationSize.MD : PaginationSize.LG}
                                siblingsCount={isMobile ? 0 : 1}
                            />
                            {expandedOffer && (
                                <Button
                                    variant="outlined_secondary"
                                    iconLeft={ArrowIcon}
                                    onClick={() => {
                                        scrollToTop();
                                        propertyConfiguratorAnalytics.gtm.propertyConfiguratorEvent(GTMEvent.FLAT_CONFIG_BACK);
                                        setExpandedOffer(null);
                                    }}
                                    css={backToLocationButton}
                                >
                                    Powrót do lokalizacji
                                </Button>
                            )}
                        </>
                    )}
                </div>
                <LeadModal />
                <MobileFiltersModal
                    isOpen={filtersModalOpen}
                    onClose={setFiltersModalOpen}
                    handleChange={(name, value) => {
                        propertyConfiguratorAnalytics.gtm.propertyConfiguratorEvent(GTMEvent.FLAT_CONFIG_CHANGE);
                        setCurrentPage(1);
                        getFirstProperties(1, name, value);
                        const values = getValuesWithCurrentSearchFieldState(props.formValues, name, value);
                        props.handleStoreValues(values);
                    }}
                    {...filterProps}
                />
            </WideContent>
        </>
    );
};

export const ResultsList = connect(mapStateToProps, mapActionsToProps)(ResultsListC);

function mapActionsToProps(dispatch: Dispatch): IActionsProps {
    // fetch search lists for property configurator
    const searchContentParams = {
        googlePOIs: false,
        regions: true,
        offers: false,
        vendors: false
    };
    return {
        formActions: bindActionCreators({...searchFormActions}, dispatch),
        ...bindActionCreators({fetchAllSearchLists: (searchInput: string) => fetchAllSearchLists(searchInput, {searchContent: searchContentParams})}, dispatch)
    };
}

function mapStateToProps(state: IRPStore): IStateProps {
    return {
        formValues: state.propertyConfigurator.formValues,
        propertyQuarters: state.variables.data?.property_quarters
    };
}

const wrapper = css`
    ${flex()};
    gap: ${calculateRemSize(3)};
`;

const preferencesSidebar = (theme: Theme) => css`
    ${flex()};
    ${flexDirection("column")};
    width: 100%;
    max-width: 384px;
    ${backgroundColor("white")};
    ${ph(3)};
    ${pv(4)};
    gap: ${calculateRemSize(4)};

    .checkboxesSegmentStyle {
        ${mt(3)};
    }

    @media (max-width: ${theme.breakpoints.md}) {
        display: none;
    }
`;

const resultsWrapper = css`
    ${flex()};
    ${flexDirection("column")};
    ${mb(5)};
    gap: ${calculateRemSize(2)};
`;

const bottomButton = css`
    align-self: center;
    max-height: ${calculateRemSize(5)};
    ${mb(1)};
`;

const headerStyle = css`
    ${mt(5)};
    ${mb(4)};
`;

const HeaderFilterWrapper = styled.div<{reverseDirection: boolean}>`
    ${flex("flex-end", "flex-end")};
    flex-direction: column;
    ${mt(4)};
    gap: ${calculateRemSize(4)};

    @media (min-width: ${(props) => props.theme.breakpoints.md}) {
        flex-direction: ${(props) => (props.reverseDirection ? "row-reverse" : "row")};
    }
`;

const inputWrapper = (theme: Theme) => css`
    @media (max-width: ${theme.breakpoints.md}) {
        display: none;
    }
`;

const backToLocationButton = css`
    align-self: center;
    ${mt(3)};
`;

const modalStyle = css`
    @media (min-width: ${getThemeBreakpoint().sm}) {
        min-width: 360px;
        max-height: 500px;
    }
`;
